import { API_BASE_URL } from "@/config";

/**
 * Il motivo dell'ultima uscita, tenuto sul dispositivo fino al prossimo ingresso e poi raccontato
 * al server.
 *
 * Esiste perche' delle uscite dell'app si vedeva solo il risultato (una revoca in Keycloak), mai
 * la causa: il 401 che le faceva partire non lasciava traccia da nessuna parte. Nel momento in
 * cui si esce non lo si puo' dire al server, spesso e' proprio lui a non accettare piu' il token;
 * quindi lo si scrive qui e lo si spedisce al rientro, con un token buono.
 *
 * Mai un token qui dentro: ora, motivo, percorsi ripuliti, numeri e stati. Nient'altro.
 *
 * Questo file non importa api.js di proposito: lo usa keycloak.js, e api.js importa keycloak.js.
 * Si parla al server con fetch, fuori dagli intercettori: un 401 o un 403 del secondo fattore su
 * questa chiamata non deve far uscire nessuno ne' portarlo sulla schermata del codice.
 */
export const CHIAVE_ULTIMA_USCITA = "zoelia_ultima_uscita";

/**
 * Le uscite chieste dalla persona. Tutte le altre le decide il codice: Keycloak ha rifiutato la
 * sessione, oppure il server continua a non riconoscere un token appena rinnovato.
 * Da qui dipende anche, nel sito, se un'uscita puo' cancellare i token salvati da un'altra scheda
 * (keycloak.js, logout).
 */
const MOTIVI_VOLONTARI = ["esci", "codice", "invito", "dispositivi_revocati", "password"];

// Oltre questi tentativi il resoconto si butta: se il server non lo vuole (rotta assente, errore
// fisso) riproporlo a ogni avvio non insegnerebbe niente di nuovo.
const TENTATIVI_MASSIMI = 5;

// Un pezzo di percorso cosi' lungo e opaco e' quasi sempre un gettone (check-in, invito, accesso):
// nel log del server non ci deve finire.
const PEZZO_OPACO = /^[A-Za-z0-9_.~%-]{24,}$/;

/** L'uscita l'ha chiesta la persona? */
export function eVolontaria(motivo) {
  return MOTIVI_VOLONTARI.includes(motivo);
}

/**
 * Il percorso senza query, senza frammento, senza origine e senza gettoni. La query si toglie
 * sempre perche' e' li' che viaggiano i biglietti (il canale eventi ne porta uno).
 * @param {string|null|undefined} indirizzo
 * @returns {string|null}
 */
export function percorsoPulito(indirizzo) {
  if (!indirizzo) return null;
  try {
    const percorso = String(indirizzo)
      .split(/[?#]/)[0]
      .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, "");
    return percorso
      .split("/")
      .map((pezzo) => (PEZZO_OPACO.test(pezzo) ? ":nascosto" : pezzo))
      .join("/")
      .slice(0, 200);
  } catch (e) {
    return null;
  }
}

function leggi() {
  try {
    const testo = localStorage.getItem(CHIAVE_ULTIMA_USCITA);
    return testo ? JSON.parse(testo) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Scrive il resoconto di un'uscita.
 *
 * Un'uscita volontaria NON copre un'uscita automatica non ancora spedita: quella automatica e'
 * l'unica che vale la pena capire, e l'«Esci» premuto dopo la si ritrova comunque negli eventi di
 * Keycloak. Succede quando il primo invio fallisce (per esempio il server lo nega perche' il
 * secondo fattore non e' ancora chiuso) e la persona esce prima del tentativo successivo.
 * @param {Object} resoconto
 */
export function registra(resoconto) {
  try {
    const precedente = leggi();
    if (precedente && !eVolontaria(precedente.motivo) && eVolontaria(resoconto.motivo)) {
      return;
    }
    localStorage.setItem(CHIAVE_ULTIMA_USCITA, JSON.stringify(resoconto));
  } catch (e) {
    // storage negato: si esce lo stesso, senza resoconto
  }
}

/** C'e' un resoconto da spedire? */
export function presente() {
  return leggi() !== null;
}

function butta() {
  try {
    localStorage.removeItem(CHIAVE_ULTIMA_USCITA);
  } catch (e) {
    // storage negato: pazienza
  }
}

/**
 * Spedisce il resoconto al server e, se l'ha preso, lo cancella.
 *
 * Qualunque errore si ignora: e' misura, non funzione. Il resoconto resta per il prossimo
 * ingresso, fino a TENTATIVI_MASSIMI, poi si butta. Non lancia mai.
 *
 * Un 403 non conta come tentativo: su questa rotta (area ACCOUNT, aperta a ogni ruolo) vuol dire
 * solo che il codice del secondo fattore non e' ancora stato dato, e il primo invio parte proprio
 * mentre la persona lo sta aspettando per posta. Contarlo bruciava un tentativo a ogni accesso.
 *
 * @param {string} token il token d'accesso valido di chi e' appena entrato
 * @param {string|null} sub chi e' appena entrato. Se il resoconto e' di un'altra persona (un
 *   dispositivo condiviso, un cambio d'account sull'invito) si butta senza spedirlo: il server lo
 *   scriverebbe a nome di chi lo manda.
 * @returns {Promise<'accettato'|'secondo_fattore'|'altra_persona'|'non_accettato'|null>}
 */
export async function invia(token, sub = null) {
  const resoconto = leggi();
  if (!resoconto || !token) return null;
  if (resoconto.sub && sub && resoconto.sub !== sub) {
    butta();
    return "altra_persona";
  }

  // Ne' il conto dei tentativi ne' l'identificativo servono al server: il sub lo prende dal token.
  // eslint-disable-next-line no-unused-vars
  const { tentativi, sub: _sub, ...corpo } = resoconto;
  let stato = null;
  try {
    const risposta = await fetch(`${API_BASE_URL}/account/diagnostica/uscita`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(corpo),
    });
    stato = risposta.status;
  } catch (e) {
    // rete: si riprova al prossimo ingresso
  }
  const accettato = stato !== null && stato >= 200 && stato < 300;

  try {
    // Nel frattempo potrebbe essere stata scritta un'uscita nuova: si tocca solo quella spedita.
    const attuale = leggi();
    if (!attuale || attuale.ora !== resoconto.ora || attuale.motivo !== resoconto.motivo) return null;
    if (stato === 403) return "secondo_fattore";
    const fatti = (resoconto.tentativi || 0) + 1;
    if (accettato || fatti >= TENTATIVI_MASSIMI) {
      localStorage.removeItem(CHIAVE_ULTIMA_USCITA);
    } else {
      localStorage.setItem(CHIAVE_ULTIMA_USCITA, JSON.stringify({ ...resoconto, tentativi: fatti }));
    }
  } catch (e) {
    // storage negato: pazienza
  }
  return accettato ? "accettato" : "non_accettato";
}

export default {
  CHIAVE_ULTIMA_USCITA,
  eVolontaria,
  percorsoPulito,
  registra,
  presente,
  invia,
};
